import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ModalFormContainerComponent } from '../../ui/modal-form-container/modal-form-container.component';
import { SearchInputComponent } from '../../inputs/search-input/search-input.component';
import { IEntitySegmentQuery } from '../../organization/segments/segment-utilities';
import { BroadcastMessageService } from '../../../services/broadcast/broadcast.service';
import { formatCount, formatWhen } from '../../../../utils/sms';

const PAGE_SIZE = 10;

/**
 * Pick segments as message or campaign recipients: a searchable, paged
 * checklist that keeps the choice across pages. The choice is a draft until
 * confirmed; closing the dialog leaves the caller's selection as it was.
 */
@Component({
  selector: 'add-segments-modal',
  standalone: true,
  imports: [ModalFormContainerComponent, SearchInputComponent],
  templateUrl: './add-segments-modal.component.html',
  styleUrl: './add-segments-modal.component.css',
})
export class AddSegmentsModalComponent implements OnChanges {
  private broadcastService = inject(BroadcastMessageService);
  private destroyRef = inject(DestroyRef);

  @Input() title: string = 'Choose segments';
  @Input() open: boolean = false;
  @Input() selection: IEntitySegmentQuery[] = [];
  @Output() close = new EventEmitter();
  @Output() updateSelection = new EventEmitter<IEntitySegmentQuery[]>();

  segments: IEntitySegmentQuery[] = [];
  page = 0;
  totalPages = 1;
  totalElements = 0;
  loading = false;
  loadError = '';
  searchTerm: string = '';

  private chosen = new Map<string, IEntitySegmentQuery>();
  private request?: Subscription;

  constructor() {
    this.destroyRef.onDestroy(() => this.request?.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['open'] || changes['selection']) {
      this.chosen = new Map((this.selection || []).filter(Boolean).map((segment) => [this.keyOf(segment), segment]));
    }
    if (changes['open'] && this.open) {
      this.searchTerm = '';
      this.page = 0;
      this.load();
    }
  }

  get selectedSegments(): IEntitySegmentQuery[] {
    return Array.from(this.chosen.values());
  }

  get selectedCount(): number {
    return this.chosen.size;
  }

  get rangeLabel(): string {
    if (!this.totalElements) return '';
    const from = this.page * PAGE_SIZE + 1;
    const to = Math.min((this.page + 1) * PAGE_SIZE, this.totalElements);
    return `${formatCount(from)}–${formatCount(to)} of ${formatCount(this.totalElements)}`;
  }

  get getIsAllSelected(): boolean {
    return this.segments.length > 0 && this.segments.every((segment) => this.checkedMember(segment));
  }

  get someSelected(): boolean {
    return !this.getIsAllSelected && this.segments.some((segment) => this.checkedMember(segment));
  }

  keyOf(segment: IEntitySegmentQuery): string {
    return String(segment?.id ?? segment?.uuid ?? segment?.name ?? '');
  }

  checkedMember(segment: IEntitySegmentQuery): boolean {
    return this.chosen.has(this.keyOf(segment));
  }

  select(segment: IEntitySegmentQuery) {
    const key = this.keyOf(segment);
    if (this.chosen.has(key)) this.chosen.delete(key);
    else this.chosen.set(key, segment);
  }

  toggleCheckAll() {
    const selectAll = !this.getIsAllSelected;
    this.segments.forEach((segment) => {
      const key = this.keyOf(segment);
      if (selectAll) this.chosen.set(key, segment);
      else this.chosen.delete(key);
    });
  }

  clearSelection() {
    this.chosen.clear();
  }

  updateSearchTerm(term: string) {
    this.searchTerm = term ?? '';
    this.page = 0;
    this.load();
  }

  goTo(page: number) {
    if (page < 0 || page >= this.totalPages || page === this.page) return;
    this.page = page;
    this.load();
  }

  load() {
    this.request?.unsubscribe();
    this.loading = true;
    this.loadError = '';
    const params: Record<string, any> = { page: this.page, size: PAGE_SIZE, sort: 'name,asc' };
    const term = this.searchTerm.trim();
    if (term) params['name.contains'] = term;

    this.request = this.broadcastService.searchSegments(params).subscribe({
      next: ({ items, totalElements, totalPages }) => {
        this.segments = items as IEntitySegmentQuery[];
        this.totalElements = totalElements;
        this.totalPages = totalPages;
        this.loading = false;
      },
      error: () => {
        this.segments = [];
        this.loading = false;
        this.loadError = 'Segments did not load. Check your connection and try again.';
      },
    });
  }

  created(segment: IEntitySegmentQuery): string {
    return formatWhen(segment?.createdAt ?? null);
  }

  emitSelection() {
    this.updateSelection.emit(this.selectedSegments);
    this.closeModal();
  }

  closeModal() {
    this.close.emit();
  }
}
